import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight, CheckCircle, Building2, Wrench, Heart, CreditCard, TrendingUp, Users, Shield } from 'lucide-react';

const Industries = () => {
  const [activeIndustry, setActiveIndustry] = useState('manufacturing');
  const location = useLocation();

  // Effect to handle URL hash navigation
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash && ['manufacturing', 'construction', 'healthcare', 'financial'].includes(hash)) {
      setActiveIndustry(hash);
    }
  }, [location.hash]);

  const industries = [
    {
      id: 'manufacturing',
      title: 'Manufacturing & Industrial',
      subtitle: 'Global OEM & Production Excellence',
      description: 'Transform manufacturing operations with intelligent supply chain orchestration and global distribution management.',
      icon: Building2,
      challenges: [
        'Manual management of 50,000+ SKUs across 200+ distributors',
        'Complex multi-tier distribution hierarchies',
        'International trade compliance across 40+ countries',
        'Real-time inventory synchronization challenges'
      ],
      solutions: [
        'AI agents automatically manage entire product catalogs',
        'Autonomous distributor onboarding in 30 minutes vs. 6 months',
        'Real-time pricing optimization across all channels',
        'Global supply chain synchronization with predictive analytics'
      ],
      results: [
        '300% increase in distributor onboarding speed',
        '60% reduction in pricing errors',
        '90% improvement in inventory accuracy',
        '$5M additional revenue in first year'
      ],
      caseStudy: 'Global OEM with $10B+ revenue deploying worldwide dropship enablement'
    },
    {
      id: 'construction',
      title: 'Construction & Building',
      subtitle: 'Project Orchestration Intelligence',
      description: 'Revolutionary project management with autonomous coordination of subcontractors, materials, and financing.',
      icon: Wrench,
      challenges: [
        'Complex multi-party project coordination',
        'Manual subcontractor qualification and management',
        'Material sourcing across multiple suppliers',
        'Progress-based payment and financing complexity'
      ],
      solutions: [
        'Autonomous project management from design to completion',
        'Dynamic subcontractor optimization based on performance',
        'Intelligent material sourcing with cost optimization',
        'Automated milestone payment processing'
      ],
      results: [
        '85% reduction in project coordination time',
        '70% improvement in subcontractor performance',
        '60% faster material procurement',
        '40% reduction in project delays'
      ],
      caseStudy: 'Major construction firm managing $2M+ in annual projects'
    },
    {
      id: 'healthcare',
      title: 'Healthcare & Medical',
      subtitle: 'Patient-Centric Supply Chain',
      description: 'Intelligent healthcare supply chain management with compliance automation and patient outcome optimization.',
      icon: Heart,
      challenges: [
        'Strict regulatory compliance requirements',
        'Critical inventory management for patient safety',
        'Complex multi-provider care coordination',
        'Insurance and billing complexity'
      ],
      solutions: [
        'Automated compliance verification and reporting',
        'Predictive inventory management for critical supplies',
        'Multi-provider coordination with patient outcome tracking',
        'Intelligent insurance processing and billing automation'
      ],
      results: [
        '95% improvement in compliance accuracy',
        '80% reduction in supply stockouts',
        '90% faster insurance claim processing',
        '70% improvement in patient outcome tracking'
      ],
      caseStudy: 'Healthcare network serving 5M+ patients across multiple states'
    },
    {
      id: 'financial',
      title: 'Financial Services',
      subtitle: 'Next-Generation FinTech Operations',
      description: 'Revolutionary financial services automation with embedded banking and intelligent risk management.',
      icon: CreditCard,
      challenges: [
        'Manual processing of 100,000+ applications annually',
        'Complex multi-party transaction financing',
        'Real-time risk assessment at scale',
        'Regulatory compliance across multiple jurisdictions'
      ],
      solutions: [
        'AI agents evaluate and approve financing in 60 seconds',
        'Multi-party transaction coordination with embedded compliance',
        'Real-time risk assessment with predictive modeling',
        'Automated regulatory reporting across all jurisdictions'
      ],
      results: [
        '95% reduction in application processing time',
        '70% increase in approval rates',
        '40% reduction in default rates',
        '$2M+ additional financing volume in first year'
      ],
      caseStudy: 'Top-10 global bank integrating supply chain finance automation'
    }
  ];

  const currentIndustry = industries.find(industry => industry.id === activeIndustry);
  const IconComponent = currentIndustry.icon;

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-purple-600 via-purple-700 to-purple-800 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Industry Solutions
          </h1>
          <p className="text-xl md:text-2xl text-purple-100 mb-8 max-w-4xl mx-auto">
            Purpose-built agent-driven solutions for the unique challenges and requirements of your industry.
          </p>
        </div>
      </section>

      {/* Industry Navigation */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Industry Tabs */}
          <div className="flex flex-wrap justify-center mb-16 border-b border-gray-200">
            {industries.map((industry) => {
              const TabIcon = industry.icon;
              return (
                <button
                  key={industry.id}
                  onClick={() => setActiveIndustry(industry.id)}
                  className={`flex items-center px-6 py-4 text-sm font-medium border-b-2 mr-8 mb-4 transition-colors duration-200 ${
                    activeIndustry === industry.id
                      ? 'border-purple-600 text-purple-600'
                      : 'border-transparent text-gray-500 hover:text-purple-600 hover:border-purple-300'
                  }`}
                >
                  <TabIcon className="w-5 h-5 mr-2" />
                  {industry.title.split(' ')[0]}
                </button>
              );
            })}
          </div>

          {/* Active Industry Content */}
          <div className="mb-16">
            <div className="text-center mb-12">
              <div className="mx-auto w-20 h-20 bg-gradient-to-br from-purple-100 to-purple-200 rounded-2xl flex items-center justify-center mb-6">
                <IconComponent className="w-10 h-10 text-purple-600" />
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                {currentIndustry.title}
              </h2>
              <p className="text-xl text-purple-600 font-semibold mb-4">
                {currentIndustry.subtitle}
              </p>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                {currentIndustry.description}
              </p>
            </div>

            {/* Challenges, Solutions, Results Grid */}
            <div className="grid lg:grid-cols-3 gap-8 mb-16">
              {/* Challenges */}
              <div className="bg-red-50 border border-red-200 rounded-xl p-8">
                <h3 className="text-2xl font-bold text-red-900 mb-6 flex items-center">
                  <svg className="w-6 h-6 mr-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  Industry Challenges
                </h3>
                <ul className="space-y-3">
                  {currentIndustry.challenges.map((challenge, index) => (
                    <li key={index} className="flex items-start space-x-3">
                      <svg className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                      <span className="text-red-800 text-sm">{challenge}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Solutions */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-8">
                <h3 className="text-2xl font-bold text-blue-900 mb-6 flex items-center">
                  <TrendingUp className="w-6 h-6 mr-3" />
                  CredFlexi Solutions
                </h3>
                <ul className="space-y-3">
                  {currentIndustry.solutions.map((solution, index) => (
                    <li key={index} className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
                      <span className="text-blue-800 text-sm">{solution}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Results */}
              <div className="bg-green-50 border border-green-200 rounded-xl p-8">
                <h3 className="text-2xl font-bold text-green-900 mb-6 flex items-center">
                  <Users className="w-6 h-6 mr-3" />
                  Proven Results
                </h3>
                <ul className="space-y-3">
                  {currentIndustry.results.map((result, index) => (
                    <li key={index} className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-green-800 text-sm font-semibold">{result}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Case Study Highlight */}
      <section className="py-16 bg-gradient-to-br from-purple-50 to-purple-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl p-8 md:p-12 shadow-xl">
            <div className="text-center mb-8">
              <h3 className="text-3xl font-bold text-gray-900 mb-4">
                Case Study Spotlight
              </h3>
              <p className="text-lg text-gray-600">
                {currentIndustry.caseStudy}
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h4 className="text-xl font-bold text-gray-900 mb-4">Implementation Highlights</h4>
                <ul className="space-y-2">
                  {currentIndustry.solutions.slice(0, 3).map((solution, index) => (
                    <li key={index} className="flex items-start space-x-3">
                      <CheckCircle className="w-4 h-4 text-purple-600 mt-1 flex-shrink-0" />
                      <span className="text-gray-700 text-sm">{solution}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              <div>
                <h4 className="text-xl font-bold text-gray-900 mb-4">Quantified Impact</h4>
                <div className="grid grid-cols-2 gap-4">
                  {currentIndustry.results.slice(0, 4).map((result, index) => (
                    <div key={index} className="text-center p-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg">
                      <div className="text-lg font-bold text-purple-600 mb-1">
                        {result.split(' ')[0]}
                      </div>
                      <div className="text-xs text-gray-600">
                        {result.split(' ').slice(1).join(' ')}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="text-center mt-8">
              <Link
                to="/contact"
                className="inline-flex items-center bg-gradient-to-r from-purple-600 to-purple-700 text-white px-8 py-3 rounded-lg font-semibold hover:from-purple-700 hover:to-purple-800 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                Get Similar Results
                <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Industry-Specific Features */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Industry-Specific Capabilities
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto">
              Purpose-built features designed for the unique requirements and regulations of your industry.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                title: 'Regulatory Compliance',
                description: 'Automatic compliance with industry-specific regulations and standards',
                icon: Shield
              },
              {
                title: 'Professional Certification',
                description: 'Integrated management of licenses, certifications, and credentials',
                icon: CheckCircle
              },
              {
                title: 'Quality Assurance',
                description: 'AI-powered quality control and performance monitoring',
                icon: TrendingUp
              },
              {
                title: 'Vertical Integration',
                description: 'Deep integration with industry-specific systems and workflows',
                icon: Building2
              }
            ].map((capability, index) => {
              const CapabilityIcon = capability.icon;
              return (
                <div key={index} className="text-center">
                  <div className="mx-auto w-16 h-16 bg-gradient-to-br from-purple-100 to-purple-200 rounded-xl flex items-center justify-center mb-4">
                    <CapabilityIcon className="w-8 h-8 text-purple-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{capability.title}</h3>
                  <p className="text-gray-600 text-sm">{capability.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-purple-600 to-purple-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Transform Your Industry Operations
          </h2>
          <p className="text-xl text-purple-100 mb-8 max-w-3xl mx-auto">
            See how CredFlexi can revolutionize your industry-specific operations with our proven solutions.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-white text-purple-600 hover:bg-purple-50 font-semibold text-lg px-8 py-4 rounded-lg shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300 inline-flex items-center justify-center"
            >
              Schedule Industry Demo
              <ArrowRight className="ml-3 h-5 w-5" />
            </Link>
            <Link
              to="/solutions"
              className="border-2 border-white/30 text-white hover:bg-white/10 font-semibold text-lg px-8 py-4 rounded-lg transition-all duration-300 inline-flex items-center justify-center backdrop-blur-sm"
            >
              Explore Solutions
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Industries;
